import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleModal from 'components/SimpleModal';
import { useCallback, useState } from 'react';
import { Tenant } from 'TenantModule/types';
import { useTenants } from './useTenants';

type ArchiveModalArgs = {
  tenantIds: number[];
  onArchiveTenantSuccess?: VoidFunction;
};

// A real component (not a render-prop closure) so the force checkbox can hold state.
const ArchiveTenantConfirmationModal = ({
  tenants,
  onClose,
  onConfirmArchive,
}: {
  tenants: Tenant[];
  onClose: VoidFunction;
  onConfirmArchive: (args: { force: boolean }) => void;
}) => {
  const [force, setForce] = useState(false);
  // The server rejects bulk force: the live-realm override is single-tenant only
  // (GLX-1819), so the checkbox only exists for a single selection.
  const canForce = tenants.length === 1;

  return (
    <SimpleModal
      onClose={onClose}
      open
      onConfirm={() => onConfirmArchive({ force })}
      closeOnConfirm
      // Zero resolved tenants would make the archive a silent no-op.
      disableConfirmButton={tenants.length === 0}
      Body={
        <div>
          <div>
            You are about to archive <b>{tenants.length}</b>{' '}
            {tenants.length > 1 ? 'Tenants' : 'Tenant'}.
          </div>
          <Box
            component="ul"
            sx={{
              maxHeight: 350,
              overflow: 'auto',
            }}
          >
            {tenants.map((tenant) => {
              return (
                <li key={tenant.id}>
                  Id: {tenant.id}, Name: {tenant.tenantName}
                </li>
              );
            })}
          </Box>
          <Box
            sx={{
              margin: '1rem 0',
            }}
          >
            Archived tenants keep their data and can be restored with Unarchive. Archiving is
            rejected while a tenant still has live realms; only <b>Decommission</b> (from the
            Lifecycle menu, after archiving) permanently removes resources.
          </Box>
          {canForce && (
            <FormControlLabel
              control={<Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} />}
              label="Force: archive even with live realms (they stay stranded until the tenant is unarchived)"
            />
          )}
        </div>
      }
    />
  );
};

export const useArchiveTenantConfirmationModal = () => {
  const { openModal } = useDynamicModal();
  const { getTenant, bulkArchiveTenant } = useTenants();

  const openArchiveTenantConfirmationModal = useCallback(
    (args: ArchiveModalArgs) => {
      openModal({
        modal: ({ isOpen: _isOpen, toggle }) => {
          const tenants = args.tenantIds.flatMap((tenantId) => getTenant({ tenantId }) || []);

          return (
            <ArchiveTenantConfirmationModal
              tenants={tenants}
              onClose={toggle}
              onConfirmArchive={({ force }) => bulkArchiveTenant({ ...args, force })}
            />
          );
        },
      });
    },
    [bulkArchiveTenant, getTenant, openModal],
  );
  return {
    openArchiveTenantConfirmationModal,
  };
};
